/**
 * Legacy Wix → new-canonical URL redirect map (client-side mirror).
 *
 * This mirrors ``LEGACY_REDIRECTS`` in ``/app/backend/prerender.py`` so the
 * React SPA can 301-equivalent (client-side ``<Navigate replace />``) users
 * that arrived on a legacy URL directly. When the two files drift the
 * backend is the source of truth — the backend also writes a static HTML
 * redirect stub at every source path so bots/crawlers get an actual 301.
 *
 * Every entry preserves an organic ranking captured in the 2026-07 GSC +
 * SEMrush export. See ``/app/scripts/seo_data/url_preservation_report.md``.
 */
const DEPRECATED_SUFFIX = "-" + "old";

const LEGACY_REDIRECTS = [
  ["/privacy-policy", "/privacy"],
  ["/terms-and-conditions", "/terms"],
  ["/es/politica-de-privacidad", "/es/privacidad"],
  ["/es/terminos-y-condiciones", "/es/terminos"],
  ["/derecho-de-familia", "/es/derecho-de-familia"],
  ["/derecho-empresarial", "/es/derecho-empresarial"],
  ["/acerca-de-nosotros", "/es/acerca-de-nosotros"],
  ["/contactenos", "/es/contactenos"],
  ["/ubicaciones", "/es/ubicaciones"],
  ["/contact-us" + DEPRECATED_SUFFIX, "/contact-us"],

  // --- EN legacy blog/category paths ---
  ["/blog/categories/texas-family-law", "/blog"],

  // --- EN blogs → topical new blogs / service pages ---
  // 2026-09-22 — Phase 28: removed redirect for /post/trump-law-child-support-taxes
  // which now serves a real self-canonical post (revived legacy URL).
  ["/post/trump-tariffs-child-support", "/post/trump-child-support-law-what-dallas-parents-should-know"],
  ["/post/calculating-child-support-income", "/family-law/child-support"],
  ["/post/change-amount-child-support", "/family-law/child-support"],
  ["/post/fail-spousal-support", "/family-law/alimony"],
  ["/post/how-to-pay-child-support-debt", "/family-law/child-support"],
  ["/post/how-to-report-non-payment-of-child-support-in-texas", "/family-law/child-support"],
  ["/post/non-payment-child-support", "/family-law/child-support"],
  ["/post/parent-paying-child-support", "/family-law/child-support"],
  ["/post/remarriage-child-support-payments", "/family-law/child-support"],
  ["/post/report-unpaid-child-support", "/family-law/child-support"],
  ["/post/when-does-child-support-end", "/family-law/child-support"],

  ["/post/how-to-obtain-a-restraining-order-in-texas", "/post/protective-orders-dallas-what-you-need-to-know"],
  ["/post/restraining-order-texas", "/post/protective-orders-dallas-what-you-need-to-know"],
  ["/post/orden-restriccion-texas", "/post/protective-orders-dallas-what-you-need-to-know"],
  ["/post/dallas-protection-order-lawyer", "/post/protective-orders-dallas-what-you-need-to-know"],
  // 2026-08-26 — Phase 23: removed redirect for
  // /post/understanding-the-difference-between-restraining-orders-and-protective-orders
  // which now serves a real self-canonical comparison article.
  ["/post/covert-abuse-victim", "/post/protective-orders-dallas-what-you-need-to-know"],
  // 2026-09-22 — Phase 28: removed redirect for
  // /post/recognizing-the-signs-understanding-the-7-indicators-of-emotional-abuse
  // which now serves a real self-canonical post (revived legacy URL).
  ["/post/report-abuse-not-victim", "/post/protective-orders-dallas-what-you-need-to-know"],
  ["/post/spouse-threatens-me-with-gun", "/post/protective-orders-dallas-what-you-need-to-know"],
  ["/post/can-i-testify-about-abuse", "/post/protective-orders-dallas-what-you-need-to-know"],

  ["/post/cps-can-do-texas", "/family-law/domestic-violence"],
  ["/post/grounds-cps-remove-child", "/family-law/domestic-violence"],
  ["/post/understanding-the-powers-and-limitations-of-cps-in-texas", "/family-law/domestic-violence"],
  ["/post/what-happens-when-you-are-reported-to-child-protective-services-in-texas", "/family-law/domestic-violence"],
  ["/post/razones-cps-remover-ninos", "/family-law/domestic-violence"],

  ["/post/how-establish-paternity", "/family-law/child-custody"],
  ["/post/drugs-abuse-and-child-custody", "/post/how-does-parental-alienation-impact-child-custody-decisions-in-texas"],
  ["/post/does-adultery-impact-child-custody-decisions-in-texas", "/post/how-does-parental-alienation-impact-child-custody-decisions-in-texas"],
  ["/post/how-can-i-protect-my-child-from-an-alcoholic-co-parent", "/post/communication-toxic-ex-custody"],
  ["/post/navigating-texas-child-custody-laws-when-moving-out-of-state", "/post/how-to-file-for-child-custody-in-texas-a-step-by-step-guide"],

  ["/post/birth-parents-adoption-texas", "/post/stepparent-and-grandparent-adoption-in-texas"],
  ["/post/how-adopt-child-family-member", "/post/stepparent-and-grandparent-adoption-in-texas"],
  ["/post/how-to-become-legal-guardian", "/post/stepparent-and-grandparent-adoption-in-texas"],
  ["/post/step-parent-custody-texas", "/post/stepparent-and-grandparent-adoption-in-texas"],
  ["/post/stepparent-grandparent-adoptions", "/post/stepparent-and-grandparent-adoption-in-texas"],

  ["/post/top-dallas-divorce-attorneys", "/post/dallas-divorce-lawyer-child-custody-attorney-in-dallas"],
  ["/post/dallas-divorce-lawyer-7-year-common-law-marriage", "/family-law/common-law-marriages"],
  ["/post/dallas-divorce-lawyer-for-high-asset-or-high-net-worth-divorces-garcia-windsor-p-c", "/family-law/high-asset-divorce"],
  ["/post/dallas-divorce-lawyer-top-10-questions-about-a-dallas-county-divorce", "/family-law/divorce-laws"],
  ["/post/dallas-divorce-lawyer-why-a-man-should-choose-a-female-divorce-attorney", "/family-law/divorce-laws"],
  ["/post/dallas-top-family-law-firms", "/family-law"],
  ["/post/divorce-top-dallas-family-law-firm", "/post/dallas-divorce-lawyer-child-custody-attorney-in-dallas"],
  ["/post/does-my-husband-have-to-pay-the-bills-until-we-are-divorced", "/family-law/divorce-laws"],
  ["/post/key-questions-divorce-lawyer", "/post/dallas-divorce-lawyer-child-custody-attorney-in-dallas"],
  ["/post/reverse-my-divorce-in-texas", "/family-law/divorce-laws"],
  ["/post/request-new-trial-divorce", "/family-law/contested-divorce"],
  ["/post/who-pays-bills-during-divorce", "/family-law/divorce-laws"],
  ["/post/can-i-get-divorced-in-texas-if-i-was-married-abroad", "/family-law/divorce-laws"],

  ["/post/are-my-separate-bank-accounts-considered-marital-property-in-texas", "/post/dallas-property-division-community-vs-separate"],
  ["/post/dallas-divorce-lawyer-division-of-assets-in-texas-divorce", "/post/dallas-property-division-community-vs-separate"],
  ["/post/division-benefits-military-retirement", "/family-law/property-assets"],

  ["/post/postnuptial-agreement-divorce", "/family-law/prenuptial-postnuptial-agreements"],
  ["/post/what-is-legal-separation-in-texas", "/post/legal-separation-vs-divorce-in-texas-what-your-options-really-are"],
  ["/post/protect-child-deportation", "/family-law/domestic-violence"],

  // --- ES legacy paths ---
  ["/es/blog/categories/leyes-de-texas-1", "/es/blog"],
  ["/es/blog/categories/manutencion-conyugal", "/es/blog"],
  ["/es/acerca-de-nosotros/isabela-garcia", "/es/acerca-de-nosotros"],
  ["/es/derecho-de-familia/testamentos-y-trusts", "/es/derecho-de-familia/testamentos-y-fideicomisos"],

  // 2026-09-22 — Phase 28: removed redirect for /es/post/ley-de-trump-manutencion-taxes
  // which now serves a real self-canonical post (Spanish pair of the revived EN post).
  ["/es/post/aranceles-trump-child-support", "/es/post/nueva-ley-manutencion-hijos-dallas"],
  ["/es/post/calculando-manutencion-infantil", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/es/post/cambiar-monto-child-support", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/es/post/child-support-custodia-compartida", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/es/post/child-support-gastos-hijo", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/es/post/consecuencias-impago-child-support", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/es/post/padre-paga-child-support", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/es/post/renunciar-al-child-support", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/es/post/reportar-impago-child-support", "/es/derecho-de-familia/manutencion-de-los-hijos"],

  ["/es/post/como-orden-restriccion-proteccion-en-dallas", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/orden-restriccion-texas", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/abogado-de-ordenes-de-proteccion-en-dallas", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/pareja-amenaza-con-arma", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/victima-abuso-encubierto", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/denunciar-caso-violencia-domestica", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/llevar-caso-violencia-domestica", "/es/post/proteccion-legal-violencia-domestica-dallas"],

  ["/es/post/cps-puede-hacer-texas", "/es/derecho-de-familia/violencia-domestica"],
  ["/es/post/cps-investigacion-de-violencia-domestica", "/es/derecho-de-familia/violencia-domestica"],

  ["/es/post/abuso-drogas-custodia-hijos", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/como-establecer-paternidad", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/custodia-compartida-en-dallas-texas", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/puedo-usar-los-mensajes-de-texto-insultantes-como-evidencia-en-el-tribunal", "/es/derecho-de-familia/custodia-infantil"],

  ["/es/post/abogado-de-dallas-para-la-tutela-de-abuelos", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],
  ["/es/post/como-adoptar-nino-familiar", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],
  ["/es/post/como-convertirme-en-guardian-legal", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],
  ["/es/post/padrastro-custodia-texas", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],
  ["/es/post/superando-barreras-adopcion", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],

  // [Phase 34, 2026-10-06] /es/post/afecta-infidelidad-caso-divorcio revived
  // as a live post — its interim redirect entry was removed.
  ["/es/post/divorcio-ex-paradero-desconocido-1", "/es/derecho-de-familia/divorcio"],
  ["/es/post/el-marido-paga-las-facturas-el-divorcio-es-definitivo", "/es/derecho-de-familia/divorcio"],
  ["/es/post/finalizar-divorcio-rapido-texas", "/es/derecho-de-familia/divorcio"],
  ["/es/post/mejores-abogados-divorcio-dallas", "/es/derecho-de-familia/divorcio"],
  ["/es/post/mejores-firmas-abogados-dallas", "/es/derecho-de-familia"],
  ["/es/post/navegando-costos-divorcio", "/es/derecho-de-familia/divorcio"],
  ["/es/post/revertir-mi-divorcio-en-texas", "/es/derecho-de-familia/divorcio"],

  ["/es/post/division-beneficios-jubilacion-militar", "/es/derecho-de-familia/activos-inmobiliarios"],
  ["/es/post/division-de-bienes-internacionales-en-caso-de-divorcio", "/es/derecho-de-familia/activos-inmobiliarios"],

  ["/es/post/califico-para-recibir-manutencion-conyugal-en-texas", "/es/derecho-de-familia/pension-alimenticia"],
  ["/es/post/manutencion-conyugal-sin-pago", "/es/derecho-de-familia/pension-alimenticia"],
  ["/es/post/segundo-matrimonio-pension", "/es/derecho-de-familia/pension-alimenticia"],

  ["/es/post/acuerdo-postnupcial-despues-matrimonio", "/es/derecho-de-familia/acuerdos-prenupciales-y-postnupciales"],
  ["/es/post/acuerdo-postnupcial-divorcio", "/es/derecho-de-familia/acuerdos-prenupciales-y-postnupciales"],

  ["/es/post/estoy-casado-en-union-libre-en-texas", "/es/post/matrimonio-informal-en-texas-cuando-esta-legalmente-casado"],

  ["/es/post/trump-impulsa-auto-deportacion", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/proteger-hijo-deportacion", "/es/derecho-de-familia/violencia-domestica"],
  ["/es/post/puedo-evitar-deportacion-familia", "/es/derecho-de-familia/violencia-domestica"],

  // ------------------------------------------------------------
  // Phase 29 (2026-09-22) — GSC Performance export mapping (139 URLs).
  // Mirror of the backend list in prerender.py; see that file for the
  // full provenance note. Client-side fallback only — the real 301s
  // live at the Cloudflare edge.
  // ------------------------------------------------------------
  ["/post/alimony-in-texas-spousal-support-divorce", "/family-law/alimony"],
  ["/post/can-i-waive-child-support", "/family-law/child-support"],
  ["/post/dallas-leading-divorce-law-firm", "/family-law"],
  ["/post/vawa-divorce-lawyers-in-dallas-county-texas", "/family-law/domestic-violence"],
  ["/post/modify-a-child-custody-agreement-in-texas", "/family-law/child-custody"],
  ["/post/ex-sent-me-insulting-texts-can-i-use-them-as-evidence-in-court", "/post/communication-toxic-ex-custody"],
  ["/post/when-the-other-party-fails-to-comply-steps-to-enforce-court-orders-in-texas", "/family-law/child-support"],
  ["/post/protecting-your-business-during-divorce-navigating-impact-on-partnerships-and-stakeholders", "/family-law/high-asset-divorce"],
  ["/post/navigating-the-tax-implications-of-dividing-business-assets-in-divorce", "/family-law/property-assets"],
  ["/post/navigating-the-adoption-process-in-texas-the-requirements-and-challenges", "/post/stepparent-and-grandparent-adoption-in-texas"],
  ["/post/do-you-qualify-for-common-law-marriage", "/post/common-law-marriage-in-texas-when-are-you-legally-married"],
  ["/post/protect-child-inheritance-divorce", "/post/estate-planning-after-a-dallas-divorce-wills-and-trusts"],
  ["/post/divorce-ex-whereabouts-unknown", "/family-law/divorce-laws"],
  ["/post/dallas-divorce-lawyers-happy-2006", "/blog"],
  ["/post/recognizing-red-flags-in-divorce-what-you-should-never-tolerate-from-your-spouse", "/post/recognizing-the-signs-understanding-the-7-indicators-of-emotional-abuse"],
  ["/post/dallas-divorce-lawyer-top-10-questions-about-prenuptial-agreements-in-texas", "/family-law/prenuptial-postnuptial-agreements"],
  ["/post/is-it-possible-to-divorce-without-dividing-assets-exploring-asset-protection-in-texas-divorces", "/post/dallas-property-division-community-vs-separate"],
  ["/post/understanding-the-timeline-for-an-agreed-divorce-in-texas-can-you-get-an-immediate-divorce", "/family-law/divorce-laws"],
  ["/post/spotting-the-red-flags-common-phrases-narcissists-use-during-divorce", "/post/recognizing-the-signs-understanding-the-7-indicators-of-emotional-abuse"],
  ["/post/lgbtq-parental-rights", "/post/same-sex-divorce-in-texas-lgbtq-family-law-after-obergefell"],
  ["/post/can-a-spouse-refuse-a-divorce-in-texas", "/family-law/contested-divorce"],
  ["/post/questions-for-dallas-divorce-lawyers-high-net-worth-divorces-part-2", "/family-law/high-asset-divorce"],
  ["/post/does-infidelity-affect-divorce", "/family-law/divorce-laws"],
  ["/post/domestic-violence-protection", "/family-law/domestic-violence"],
  ["/post/common-misconceptions-alimony", "/family-law/alimony"],
  ["/post/could-my-actions-trigger-a-cps-investigation", "/family-law/domestic-violence"],
  ["/post/secure-rights-grandparent-visitation-custody-dallas-texas", "/family-law/grandparents-stepparent-adoptions"],
  ["/post/30-questions-for-dallas-divorce-lawyers-high-asset-high-net-worth-divorces", "/family-law/high-asset-divorce"],
  ["/post/child-support-joint-custody", "/family-law/child-support"],
  ["/post/quickly-finalize-a-divorce", "/family-law/divorce-laws"],
  ["/post/should-i-consider-a-postnuptial-agreement-after-marriage", "/family-law/prenuptial-postnuptial-agreements"],
  ["/post/Dallas-common-law-marriage-attorney", "/family-law/common-law-marriages"],
  ["/post/can-i-abusesigned-an-nda", "/business-law"],
  ["/post/digital-assets-division-divorces", "/family-law/property-assets"],
  ["/post/save-family-from-deportation", "/family-law/domestic-violence"],
  ["/post/can-you-be-sued-for-child-support-without-your-knowledge", "/family-law/child-support"],
  ["/post/navigating-divorce-in-same-sex-marriages-what-you-need-to-know", "/post/same-sex-divorce-in-texas-lgbtq-family-law-after-obergefell"],
  ["/post/unveiling-the-process-understanding-discovery-in-divorce", "/family-law/contested-divorce"],
  ["/post/dallas-lawyers-wills-trusts-immigrants", "/family-law/wills-and-trusts-dallas-lawyers"],
  ["/post/intentional-delays-in-divorce-tactics-and-timelines", "/family-law/contested-divorce"],
  ["/post/navigating-immigration-divorce-and-custody-issues-in-texas-legal-insights-from-garciawindsor", "/family-law/divorce-laws"],
  ["/post/supreme-court-same-sex-marriage", "/post/same-sex-divorce-in-texas-lgbtq-family-law-after-obergefell"],
  ["/post/co-parenting-successfully-tips-for-effective-communication-and-conflict-resolution", "/post/communication-toxic-ex-custody"],
  ["/post/understanding-common-law-marriage-in-texas-what-you-need-to-know", "/post/common-law-marriage-in-texas-when-are-you-legally-married"],
  ["/post/paternity-rights-under-texas-family-law-a-comprehensive-guide", "/family-law/child-custody"],
  ["/post/enforcing-unpaid-obligations-after-a-texas-divorce", "/family-law/child-support"],
  ["/post/can-i-be-forced-to-leave-my-home-if-i-get-divorced-in-texas", "/family-law/divorce-laws"],
  ["/post/evidence-domestic-violence", "/family-law/domestic-violence"],
  ["/post/child-custody-modifications-in-texas-what-parents-need-to-know", "/family-law/child-custody"],
  ["/post/stepparent-adoptions-in-dallas-texas", "/post/stepparent-and-grandparent-adoption-in-texas"],
  ["/post/take-domestic-violence-court", "/family-law/domestic-violence"],
  ["/post/take-a-look-at-attorney-isabela-amie-garcia-s-interview-with-univision-news-anchor-luis-prado-rega", "/about-us/press"],
  ["/post/selected-for-2020-super-lawyers", "/about-us/press"],
  ["/post/univision-primer-impacto-interviews-attorney-garcia", "/about-us/press"],
  ["/post/nafla-awards-attorney-isabela-garcia-top-ten-ranking-2018-title", "/about-us/press"],
  ["/post/attorney-isabela-amie-garcia-recognized-as-a-top-expert-of-law-by-continental-who-s-who", "/about-us/press"],
  ["/post/manutencion-conyugal-sin-pago", "/es/derecho-de-familia/pension-alimenticia"],
  ["/post/victima-abuso-encubierto", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/post/abuso-drogas-custodia-hijos", "/es/derecho-de-familia/custodia-infantil"],
  ["/post/padrastro-custodia-texas", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],
  ["/post/cuando-termina-child-support", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/post/child-support-custodia-compartida", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/post/afecta-infidelidad-caso-divorcio", "/es/post/afecta-infidelidad-caso-divorcio"],
  ["/post/reportar-impago-child-support", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/post/como-pagar-deuda-manutencion", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/post/padres-adopcion-texas", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],
  ["/blog/categories/female-divorce-lawyer", "/blog"],
  ["/blog/categories/high-asset-divorce-lawyer", "/blog"],
  ["/blog/categories/prenuptial-agreement", "/blog"],
  ["/blog/categories/dallas-divorce-lawyer", "/blog"],
  ["/blog/categories/separate-bank-accounts", "/blog"],
  ["/blog/categories/hispanic-business", "/blog"],
  ["/blog/categories/holidays-1", "/blog"],
  ["/blog/categories/contested-divorce-1", "/blog"],
  ["/blog/categories/adoption-1", "/blog"],
  ["/blog/categories/remarriage-child-support", "/blog"],
  ["/blog/categories/digital-assets", "/blog"],
  ["/blog/categories/emotional-abuse", "/blog"],
  ["/blog/categories/high-net-worth-divorce", "/blog"],
  ["/blog/categories/non-disclosure-agreements", "/blog"],
  ["/blog/categories/dallas-divorce-lawyer-questions", "/blog"],
  ["/es/post/quien-sufre-mas-divorcio", "/es/derecho-de-familia/divorcio"],
  ["/es/post/quien-paga-cuentas-divorcio", "/es/derecho-de-familia/divorcio"],
  ["/es/post/matrimonio-union-libre-texas", "/es/post/matrimonio-informal-en-texas-cuando-esta-legalmente-casado"],
  ["/es/post/navegando-la-custodia", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/preguntas-abogado-divorcio", "/es/post/abogado-de-divorcios-en-dallas-derecho-familiar"],
  ["/es/post/entendiendo-leyes-custodia-menores", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/proteger-herencia-hijo-divorcio", "/es/post/planificacion-patrimonial-despues-de-un-divorcio-en-dallas"],
  ["/es/post/publicar-redes-sociales-divorcio", "/es/derecho-de-familia/divorcio"],
  ["/es/post/se-consideran-mis-cuentas-bancarias-individuales-como-propiedad-matrimonial-en-dallas-texas", "/es/post/division-bienes-activos-divorcio"],
  ["/es/post/como-pagar-deuda-manutencion", "/es/derecho-de-familia/manutencion-de-los-hijos"],
  ["/es/post/visitas-la-custodia-de-mis-nietos-elcondado-dallas", "/es/derecho-de-familia/abuelo-padrastro-adoptivo"],
  ["/es/post/solicitar-orden-judicial-visitas", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/abogada-de-divorcio-en-dallas", "/es/post/abogado-de-divorcios-en-dallas-derecho-familiar"],
  ["/es/post/razones-cps-remover-ninos", "/es/derecho-de-familia/violencia-domestica"],
  ["/es/post/abogados-de-custodia-en-dallas", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/introduccion-violencia-familiar", "/es/derecho-de-familia/violencia-domestica"],
  ["/es/post/pareja-no-revela-bienes", "/es/post/acuerdos-prenupciales-en-texas-guia-de-un-abogado-de-dallas"],
  ["/es/post/amenaza-armada-batalla-custodia", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/evidencia-violencia-domestica", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/abogado-de-divorcio-en-dallas-custodia-infantil", "/es/post/abogado-de-divorcios-en-dallas-derecho-familiar"],
  ["/es/post/mitos-comunes-pension-alimenticia", "/es/derecho-de-familia/pension-alimenticia"],
  ["/es/post/proteccion-violencia-domestica", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/abogados-de-familia-divorcio-en-el-condado-de-dallas", "/es/derecho-de-familia/divorcio"],
  ["/es/post/abogados-de-familia-haga-crecer-y-proteja-su-dinero-en-texas", "/es/post/planificacion-patrimonial-despues-de-un-divorcio-en-dallas"],
  ["/es/post/abogados-de-custodia-de-hijos-en-dallas", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/comunicacion-ex-toxico-custodia", "/es/derecho-de-familia/custodia-infantil"],
  ["/es/post/abogado-de-divorcios-en-dallas-separacion-legal", "/es/post/separacion-legal-vs-divorcio-en-texas"],
  ["/es/post/caso-familiar-pelicula-terror", "/es/derecho-de-familia"],
  ["/es/post/estrategias-de-blindaje-patrimonial-empresarial", "/es/derecho-empresarial"],
  ["/es/post/padres-adopcion-texas", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],
  ["/es/post/entendiendo-requisitos-divorcio", "/es/derecho-de-familia/divorcio"],
  ["/es/post/abogados-de-familia-pago-alimony-texas-en-dallas-texas", "/es/derecho-de-familia/pension-alimenticia"],
  ["/es/post/abogados-de-familia-prenupcial-o-postnupcial-perspectivas-del-derecho-familiar-en-dallas-texas", "/es/derecho-de-familia/acuerdos-prenupciales-y-postnupciales"],
  ["/es/post/matrimonio-informal-en-texas-cuando-esta-legales-casado", "/es/post/matrimonio-informal-en-texas-cuando-esta-legalmente-casado"],
  ["/es/post/division-activos-digitales-divorcio", "/es/derecho-de-familia/activos-inmobiliarios"],
  ["/es/post/abogados-de-familia-univision-primer-impacto", "/es/acerca-de-nosotros"],
  ["/es/post/puedo-testificar-sobre-abuso", "/es/post/proteccion-legal-violencia-domestica-dallas"],
  ["/es/post/banderas-rojas-durante-un-divorcio-contencioso", "/es/post/7-indicadores-de-abuso-emocional"],
  ["/es/post/derechos-parentales-lgbtq", "/es/post/divorcio-entre-personas-del-mismo-sexo-en-texas"],
  ["/es/post/firma-abogados-lider-dallas", "/es/derecho-de-familia"],
  ["/es/post/adopciones-padrastros-abuelos", "/es/post/adopcion-de-padrastro-y-abuelos-en-texas"],
  ["/es/post/abogados-de-divorcio-de-dallas-feliz-2006", "/es/blog"],
  ["/es/derecho-de-familia/union-libre-concubinato", "/es/derecho-de-familia/matrimonios-de-hecho"],
  ["/es/contact-us", "/es/contactenos"],
  ["/es/blog/categories/abogados", "/es/blog"],
  ["/es/blog/categories/custodia-infantil", "/es/blog"],
  ["/es/blog/categories/child-support-1", "/es/blog"],
  ["/es/blog/categories/amenaza-armada", "/es/blog"],
  ["/es/blog/categories/corte-suprema", "/es/blog"],
  ["/es/blog/categories/relación-tóxica", "/es/blog"],
  ["/es/blog/categories/firma-de-abogados", "/es/blog"],
  ["/es/blog/categories/divorcio-para-empresarios", "/es/blog"],
  ["/es/blog/categories/testamentos-y-fideicomisos", "/es/blog"],
  ["/es/blog/categories/abuso-emocional", "/es/blog"],
  ["/es/blog/categories/custodia-de-los-hijos", "/es/blog"],
  ["/es/blog/categories/firma-be-abogados", "/es/blog"],
  ["/es/blog/categories/adopción", "/es/blog"],
  ["/es/blog/categories/matrimonio-igualitario", "/es/blog"],
];

export default LEGACY_REDIRECTS;
